/**
 * Resolve a tour slug to a document.
 *
 * DB first, bundled JSON second. The bundled copy is not a stopgap — it is what
 * makes a fresh environment, a local dev run, a CI check and a database outage
 * all still have a working tour. `source` is stamped onto every event, so a
 * fallback that has silently become permanent is visible in analytics rather
 * than invisible.
 *
 * Phase 0 ships bundled-only; the API branch activates with the `fg_tours`
 * migration and is written now so nothing downstream has to change.
 */

import { ServerApiClient } from '../../data/ServerApiClient'
import { parseTourDoc } from './tourSchema'
import type { LoadedTour, TourDoc } from './tourTypes'
import cfoFirstLook from '../../tours/cfo-first-look.json'
import cfoFirstLogin from '../../tours/cfo-first-login.json'
import clientMonthClose from '../../tours/client-month-close.json'
import cfoFirstLookRestaurant from '../../tours/cfo-first-look-restaurant.json'
import cfoFirstLookLaw from '../../tours/cfo-first-look-law.json'
import cfoFirstLookSaas from '../../tours/cfo-first-look-saas.json'
import cfoFirstLookDistributor from '../../tours/cfo-first-look-distributor.json'
import cfoInHouseFirstLook from '../../tours/cfo-in-house-first-look.json'
import cfoAdvisorFirstLook from '../../tours/cfo-advisor-first-look.json'

const BUNDLED: Record<string, unknown> = {
  'cfo-first-look': cfoFirstLook,
  'cfo-first-login': cfoFirstLogin,
  'client-month-close': clientMonthClose,
  'cfo-first-look-restaurant': cfoFirstLookRestaurant,
  'cfo-first-look-law': cfoFirstLookLaw,
  'cfo-first-look-saas': cfoFirstLookSaas,
  'cfo-first-look-distributor': cfoFirstLookDistributor,
  'cfo-in-house-first-look': cfoInHouseFirstLook,
  'cfo-advisor-first-look': cfoAdvisorFirstLook,
}

/**
 * Every slug this build ships. Derived from BUNDLED rather than hand-listed, so
 * a tour added to the map is automatically covered by the round-trip test that
 * asserts each one actually loads. `client-month-close` shipped for weeks with
 * generated narration audio and never loaded once — its entry route failed
 * validation, `loadBundledTour` returned null, and no test named the slug.
 */
export const BUNDLED_TOUR_SLUGS = Object.keys(BUNDLED)

export function loadBundledTour(slug: string): LoadedTour | null {
  const raw = BUNDLED[slug]
  if (!raw) return null
  const parsed = parseTourDoc(raw)
  if (!parsed || !parsed.validation.ok) {
    if (parsed) {
      // A committed tour that fails validation is a build-time bug; say so
      // loudly in dev rather than rendering a half-broken walkthrough.
      console.error(`[tour] bundled tour "${slug}" is invalid`, parsed.validation.errors)
    }
    return null
  }
  return { doc: parsed.doc as TourDoc, source: 'bundled', version: 0 }
}

export async function loadTour(slug: string): Promise<LoadedTour | null> {
  try {
    const api = new ServerApiClient()
    const body = await api.getPublishedTour(slug)
    const parsed = parseTourDoc(body?.doc)
    if (parsed?.validation.ok) {
      return { doc: parsed.doc, source: 'db', version: Number(body?.version) || 0 }
    }
    if (parsed) {
      // A published document that no longer validates is worse than none: the
      // bundled copy is at least known-good.
      console.error(`[tour] published "${slug}" failed validation; using the bundled copy`, parsed.validation.errors)
    }
  } catch {
    /* offline, 404 before anything is published, or a malformed payload */
  }
  return loadBundledTour(slug)
}
